import { pull, pullAll } from 'lodash';
import { Framework, type IDelLayerData, type IHistoryItem } from '@/framework';
import { traverseTree } from '@/utils/tree-kit';
import type { ILayerProps } from '@/version-types';
import { RealEditMerge } from './real-edit-merge';
import {
    HistoryType,
    type IHistoryAddData,
    type IHistoryDeleteData,
    type IHistoryLockData,
    type IHistorySortData,
} from './type';

export class LayerHistory extends Framework {
    private realEditMerge: RealEditMerge;

    constructor(identity: string) {
        super(identity);
        this.realEditMerge = new RealEditMerge(this.framework);
        this.registerAddHistory();
        this.registerDeleteHistory();
        this.registerSortHistory();
        this.registerLockHistory();
    }

    /** 删除图层 */
    private registerDeleteHistory() {
        const getSendMessage = (historyItem: IHistoryItem<IHistoryDeleteData>) => {
            return {
                message: [
                    historyItem.originIndex,
                    historyItem.data.data.originLayer,
                    historyItem.data.data.historyData,
                ],
            };
        };

        this.framework.service.history.register(HistoryType.DELETE, {
            owner: 'layer/base',
            collaboration: 'supported',
            onRedoUndo: (historyItem: IHistoryItem<IHistoryDeleteData>, isRedo: boolean, isUndo: boolean) => {
                if (historyItem.isDirty) return;
                const { historyData } = historyItem.data.data;
                const { item } = historyData;
                const { oldLayer, oldLayerIndex, delAll, childIds, childIndexes, replaceLayerId, newLayer, delId } =
                    item;
                const isExtraGroup = this.framework.service.flatLayer.checkIsExtraGroup(oldLayer);
                const originIndex = historyItem.originIndex;
                const originIds = this.framework.service.frame.getLayerIds(originIndex);
                if (isRedo) {
                    if (delAll) {
                        const delIds = [...childIds, oldLayer.id];
                        pullAll(originIds, delIds);
                        delIds.forEach((id) => {
                            this.framework.service.flatLayer.delete(id);
                        });
                        this.framework.service.frame.delActiveLayer();
                    } else {
                        if (newLayer) {
                            this.framework.service.flatLayer.delete(delId);
                            pull(originIds, delId);
                            this.framework.service.frame.addOrUpdateLayer(newLayer, originIndex);
                            this.framework.service.frame.setActiveLayer(oldLayer.id);
                        } else {
                            if (replaceLayerId) {
                                originIds.splice(oldLayerIndex, 1, replaceLayerId);
                                const layer = this.framework.service.flatLayer.getLayer(replaceLayerId, true);
                                layer.parentId = undefined;
                                this.framework.service.flatLayer.delete(oldLayer.id);
                                pull(originIds, oldLayer.id);
                                this.framework.service.frame.addOrUpdateLayer(layer, originIndex);
                                this.framework.service.frame.setActiveLayer(layer.id);
                            } else {
                                pull(originIds, oldLayer.id);
                                this.framework.service.flatLayer.delete(oldLayer.id);
                                this.framework.service.frame.delActiveLayer();
                            }
                        }
                    }
                }
                if (isUndo) {
                    if (delAll) {
                        pullAll(originIds, [...childIds, oldLayer.id]);
                        if (isExtraGroup) {
                            for (let i = 0; i < childIds.length; i++) {
                                const id = childIds[i];
                                const index = childIndexes[i];
                                originIds.splice(index, 0, id);
                            }
                        }
                        originIds.splice(oldLayerIndex, 0, oldLayer.id);
                        this.framework.service.frame.addOrUpdateLayer(oldLayer, originIndex);
                        this.framework.service.frame.setActiveLayer(oldLayer.id);
                    } else {
                        if (newLayer) {
                            this.framework.service.frame.addOrUpdateLayer(oldLayer, originIndex);
                            this.framework.service.frame.setActiveLayer(delId);
                        } else {
                            if (replaceLayerId) {
                                originIds.splice(oldLayerIndex, 1, oldLayer.id);
                                this.framework.service.frame.addOrUpdateLayer(oldLayer, originIndex);
                                this.framework.service.frame.setActiveLayer(delId);
                            } else {
                                originIds.splice(oldLayerIndex, 0, oldLayer.id);
                                this.framework.service.frame.addOrUpdateLayer(oldLayer, originIndex);
                                this.framework.service.frame.setActiveLayer(delId);
                            }
                        }
                    }
                }
            },
            onBeforeRedoUndo: (historyItem: IHistoryItem<IHistoryDeleteData>, isRedo, isUndo, store) => {
                const { historyData, originLayer } = historyItem.data.data;
                const { item } = historyData;
                const { oldLayer, delAll, childIds, newLayer } = item;

                if (isUndo) {
                    if (delAll) {
                        const delIds = [...childIds, oldLayer.id];
                        for (let i = 0; i < delIds.length; i++) {
                            const id = delIds[i];
                            if (this.framework.service.flatLayer.getLayer(id)) {
                                historyItem.isDirty = true;
                                break;
                            }
                        }
                    } else {
                        if (newLayer) {
                            historyItem.isDirty = this.framework.service.flatLayer.checkLayerIsDirty({
                                originLayer: newLayer,
                                latestIsExtraGroup: this.framework.service.flatLayer.checkIsExtraGroup(newLayer),
                                latestIsFormalGroup: this.framework.service.flatLayer.checkIsFormalGroup(newLayer),
                            });
                        } else {
                            historyItem.isDirty = !!this.framework.service.flatLayer.getLayer(oldLayer.id);
                        }
                    }
                    store.set(historyItem.cursor, {
                        isDirty: historyItem.isDirty,
                    });
                }
                if (isRedo) {
                    const beforeData = store.get(historyItem.cursor);
                    if (!beforeData) {
                        historyItem.isDirty = true;
                        return;
                    }
                    historyItem.isDirty = beforeData.isDirty;
                    if (!historyItem.isDirty) {
                        historyItem.isDirty = this.framework.service.flatLayer.checkLayerIsDirty({
                            originLayer,
                            latestIsExtraGroup: this.framework.service.flatLayer.checkIsExtraGroup(originLayer),
                            latestIsFormalGroup: this.framework.service.flatLayer.checkIsFormalGroup(originLayer),
                        });
                    }
                }
            },
            onMerge: this.realEditMerge.merge.bind(this),
            onAfterWrite: getSendMessage,
            onAfterRedoUndo: getSendMessage,
            onMessage: (
                message: [number, ILayerProps, IDelLayerData],
                isRedo: boolean,
                isUndo: boolean,
                isWrite: boolean,
            ) => {
                const [originIndex, _, historyData] = message;
                const { item } = historyData;
                const { oldLayer, oldLayerIndex, delAll, childIds, childIndexes, replaceLayerId, newLayer, delId } =
                    item;
                const originIds = this.framework.service.frame.getLayerIds(originIndex);
                const isExtraGroup = this.framework.service.flatLayer.checkIsExtraGroup(oldLayer);

                if (isWrite || isRedo) {
                    let needDelActiveLayer = false;
                    const activeInfo = this.framework.service.frame.getActiveInfo();
                    const delIds = [...childIds];
                    traverseTree(oldLayer, (item) => {
                        delIds.push(item.id);
                    });
                    delIds.forEach((id) => {
                        const warpLayer = this.framework.service.flatLayer.getWrapLayer(id, true);
                        // console.log('ot del all ', { w: warpLayer?.id, a: activeInfo?.info.id });
                        if (warpLayer?.id && warpLayer.id === activeInfo?.info.id) {
                            needDelActiveLayer = true;
                        }
                    });
                    if (needDelActiveLayer) {
                        this.framework.service.frame.delActiveLayer();
                    }
                    if (delAll) {
                        pullAll(originIds, delIds);
                        delIds.forEach((id) => {
                            this.framework.service.flatLayer.delete(id);
                        });
                    } else {
                        if (newLayer) {
                            pullAll(originIds, delIds);
                            delIds.forEach((id) => {
                                this.framework.service.flatLayer.delete(id);
                            });
                            this.framework.service.frame.addOrUpdateLayer(newLayer, originIndex);
                        } else {
                            if (replaceLayerId) {
                                originIds.splice(oldLayerIndex, 1, replaceLayerId);
                                const layer = this.framework.service.flatLayer.getLayer(replaceLayerId, true);
                                layer.parentId = undefined;
                                pullAll(originIds, delIds);
                                delIds.forEach((id) => {
                                    this.framework.service.flatLayer.delete(id);
                                });
                                this.framework.service.frame.addOrUpdateLayer(layer, originIndex);
                            } else {
                                pullAll(originIds, delIds);
                                delIds.forEach((id) => {
                                    this.framework.service.flatLayer.delete(id);
                                });
                            }
                        }
                    }
                }

                if (isUndo) {
                    if (delAll) {
                        pullAll(originIds, [...childIds, oldLayer.id]);
                        if (isExtraGroup) {
                            for (let i = 0; i < childIds.length; i++) {
                                const id = childIds[i];
                                const index = childIndexes[i];
                                originIds.splice(index, 0, id);
                            }
                        }
                        originIds.splice(oldLayerIndex, 0, oldLayer.id);
                        if (isExtraGroup) {
                            oldLayer.children?.forEach((item) => {
                                item.parentId = undefined;
                                this.framework.service.frame.addOrUpdateLayer(item, originIndex);
                            });
                        } else {
                            this.framework.service.frame.addOrUpdateLayer(oldLayer, originIndex);
                        }
                    } else {
                        const updateIds: Set<string> = new Set();
                        traverseTree(oldLayer, (item) => {
                            updateIds.add(item.id);
                        });
                        if (newLayer) {
                            this.framework.service.frame.addOrUpdateLayer(oldLayer, originIndex);
                        } else {
                            if (replaceLayerId) {
                                originIds.splice(oldLayerIndex, 1, oldLayer.id);
                                this.framework.service.frame.addOrUpdateLayer(oldLayer, originIndex);
                            } else {
                                originIds.splice(oldLayerIndex, 0, oldLayer.id);
                                this.framework.service.frame.addOrUpdateLayer(oldLayer, originIndex);
                            }
                        }
                        const activeInfo = this.framework.service.frame.getActiveInfo();
                        if (
                            activeInfo &&
                            (updateIds.has(activeInfo.info.pickId) || updateIds.has(activeInfo.info.id))
                        ) {
                            this.framework.service.frame.setActiveLayer(activeInfo.info.pickId || activeInfo.info.id);
                        }
                    }
                }
            },
        });
    }

    /** 添加图层 */
    private registerAddHistory() {
        const getSendMessage = (historyItem: IHistoryItem<IHistoryAddData>) => {
            return {
                message: [historyItem.data.data],
            };
        };

        this.framework.service.history.register(HistoryType.ADD, {
            owner: 'layer/base',
            collaboration: 'supported',
            onRedoUndo: (historyItem: IHistoryItem<IHistoryAddData>, isRedo: boolean, isUndo: boolean) => {
                if (historyItem.isDirty) return;
                const { item, operate, extraInfo } = historyItem.data.data;
                const { oldActiveId, isActiveNewLayer } = operate;
                if (isRedo) {
                    this.framework.service.frame.addOrUpdateLayer(item, historyItem.originIndex);
                    if (isActiveNewLayer) {
                        this.framework.service.frame.setActiveLayer(item.id);
                    } else {
                        this.framework.service.frame.delActiveLayer();
                    }
                    if (extraInfo?.newExtraInfo) {
                        this.framework.service.frame.setExtraInfoByLayerId(item.id, extraInfo.newExtraInfo);
                    }
                }
                if (isUndo) {
                    if (oldActiveId) {
                        const oldLayer = this.framework.service.flatLayer.getLayer(oldActiveId);
                        if (oldLayer) {
                            this.framework.service.frame.setActiveLayer(oldActiveId);
                        } else {
                            this.framework.service.frame.delActiveLayer();
                        }
                    } else {
                        this.framework.service.frame.delActiveLayer();
                    }
                    traverseTree(item, (item) => {
                        this.framework.service.frame.delLayer(item.id);
                    });
                    if (extraInfo?.oldExtraInfo) {
                        this.framework.service.frame.setExtraInfoByLayerId(item.id, extraInfo.oldExtraInfo);
                    }
                }
            },
            onBeforeRedoUndo: (historyItem: IHistoryItem<IHistoryAddData>, isRedo, isUndo, store) => {
                if (isUndo) {
                    const layer = historyItem.data.data.item;
                    historyItem.isDirty = this.framework.service.flatLayer.checkLayerIsDirty({
                        originLayer: layer,
                        latestIsExtraGroup: this.framework.service.flatLayer.checkIsExtraGroup(layer),
                        latestIsFormalGroup: this.framework.service.flatLayer.checkIsFormalGroup(layer),
                    });
                    // 撤销时候记录最新值
                    const latestLayerData: Record<string, ILayerProps> = {};
                    if (!historyItem.isDirty) {
                        traverseTree(layer, (item) => {
                            latestLayerData[item.id] = this.framework.service.flatLayer.getLayer(item.id, true);
                        });
                    }
                    store.set(historyItem.cursor, {
                        isDirty: historyItem.isDirty,
                        latestLayerData: latestLayerData,
                    });
                }
                if (isRedo) {
                    const beforeData = store.get(historyItem.cursor);
                    if (!beforeData) {
                        historyItem.isDirty = true;
                        return;
                    }
                    historyItem.isDirty = beforeData.isDirty;
                    if (!historyItem.isDirty) {
                        // 重做时候恢复最新值
                        const storeData = beforeData.latestLayerData;
                        if (storeData) {
                            traverseTree(historyItem.data.data.item, (item) => {
                                const latestLayer = storeData[item.id];
                                if (latestLayer) {
                                    const oldParentId = item.parentId;
                                    Object.assign(item, latestLayer);
                                    item.parentId = oldParentId;
                                }
                            });
                        }
                    }
                }
            },
            onAfterWrite: getSendMessage,
            onAfterRedoUndo: getSendMessage,
            onMessage: (message: [IHistoryAddData], isRedo, isUndo, isWrite, _beforeData, originIndex) => {
                const [data] = message;
                const { item, extraInfo } = data;

                if (isUndo) {
                    const activeInfo = this.framework.service.frame.getActiveInfo();
                    let needRestActiveInfo = false;
                    const finalActiveIds: string[] = [];
                    if (activeInfo?.info.pickId) {
                        finalActiveIds.push(activeInfo.info.pickId);
                    }
                    if (activeInfo?.info.id) {
                        finalActiveIds.push(activeInfo.info.id);
                    }
                    traverseTree(item, (item) => {
                        const warpLayer = this.framework.service.flatLayer.getWrapLayer(item.id, true);
                        if (warpLayer && this.framework.service.flatLayer.checkIsExtraGroup(warpLayer)) {
                            this.framework.service.frame.delActiveLayer();
                        }
                        if (item.id === activeInfo?.info.id || item.id === activeInfo?.info.pickId) {
                            needRestActiveInfo = true;
                        } else {
                            if (warpLayer?.id === activeInfo?.info.id || warpLayer?.id === activeInfo?.info.pickId) {
                                needRestActiveInfo = true;
                            }
                        }
                        const res = this.framework.service.frame.delLayer(item.id);
                        // console.log('ot add onUndo del message', { id: item.id, res });
                        if (res?.operate.activeId) {
                            finalActiveIds.push(res.operate.activeId);
                        }
                    });
                    // 重新设置激活图层
                    if (needRestActiveInfo && activeInfo) {
                        for (let i = 0; i < finalActiveIds.length; i++) {
                            const id = finalActiveIds[i];
                            if (this.framework.service.flatLayer.getLayer(id)) {
                                this.framework.service.frame.setActiveLayer(id);
                                break;
                            }
                        }
                    }
                    if (extraInfo?.oldExtraInfo) {
                        this.framework.service.frame.setExtraInfoByLayerId(item.id, extraInfo.oldExtraInfo);
                    }
                }
                if (isWrite || isRedo) {
                    if (item) {
                        if (this.framework.service.flatLayer.checkIsExtraGroup(item)) {
                            item.children?.forEach((child) => {
                                child.parentId = undefined;
                                if (isWrite && child.__runtime?.fontFamilyMap) {
                                    child.__runtime.fontFamilyMap = {};
                                }
                                this.framework.service.frame.addOrUpdateLayer(child, originIndex);
                            });
                        } else {
                            if (isWrite && item.__runtime?.fontFamilyMap) {
                                item.__runtime.fontFamilyMap = {};
                            }
                            this.framework.service.frame.addOrUpdateLayer(item, originIndex);
                        }
                    }
                    if (extraInfo) {
                        this.framework.service.frame.setExtraInfoByLayerId(item.id, extraInfo.newExtraInfo);
                    }
                }
            },
        });
    }

    /** 排序图层 */
    private registerSortHistory() {
        const getSendMessage = (historyItem: IHistoryItem<IHistorySortData>) => {
            const { originIndex } = historyItem.data.data;
            return {
                message: [originIndex, this.framework.service.frame.getRenderIdsWithoutExtraGroup(originIndex)],
            };
        };

        this.framework.service.history.register(HistoryType.SORT, {
            owner: 'layer/base',
            collaboration: 'supported',
            onRedoUndo: (historyItem: IHistoryItem<IHistorySortData>, isRedo: boolean) => {
                if (historyItem.isDirty) return;

                const { oldSortList, newSortList, originIndex } = historyItem.data.data;
                const renderIds = this.framework.service.frame.getLayerIds(originIndex);

                // 确定使用哪个排序列表作为目标
                const targetList = isRedo ? newSortList : oldSortList;

                // 创建一个新的renderIds数组
                const newRenderIds = new Array(renderIds.length);

                // 首先，将所有图层ID设置为undefined
                for (let i = 0; i < renderIds.length; i++) {
                    newRenderIds[i] = undefined;
                }

                // 然后，根据targetList中的索引信息，将图层ID放置到正确的位置
                for (let i = 0; i < targetList.length; i++) {
                    const item = targetList[i];
                    newRenderIds[item.index] = item.id;
                }

                // 最后，将剩余的undefined位置填充为原始renderIds中的图层ID
                for (let i = 0; i < renderIds.length; i++) {
                    if (newRenderIds[i] === undefined) {
                        // 找到一个尚未放置的图层ID
                        for (let j = 0; j < renderIds.length; j++) {
                            const id = renderIds[j];
                            // 检查这个ID是否已经在newRenderIds中
                            if (id && !newRenderIds.includes(id)) {
                                newRenderIds[i] = id;
                                break;
                            }
                        }
                    }
                }

                // 更新renderIds
                for (let i = 0; i < newRenderIds.length; i++) {
                    renderIds[i] = newRenderIds[i];
                }

                this.framework.service.frame.emitSortLayer(originIndex);
            },
            onBeforeRedoUndo: (historyItem: IHistoryItem<IHistorySortData>, isRedo, isUndo, store) => {
                const { oldSortList, newSortList, originIndex } = historyItem.data.data;
                if (isUndo) {
                    const renderIds = this.framework.service.frame.getLayerIds(originIndex);
                    for (let i = 0; i < newSortList.length; i++) {
                        const item = newSortList[i];
                        if (item.id !== renderIds[item.index]) {
                            historyItem.isDirty = true;
                            break;
                        }
                    }
                    store.set(historyItem.cursor, {
                        isDirty: historyItem.isDirty,
                    });
                }
                if (isRedo) {
                    const beforeData = store.get(historyItem.cursor);
                    if (!beforeData) {
                        historyItem.isDirty = true;
                        return;
                    }
                    historyItem.isDirty = beforeData.isDirty;
                    if (!historyItem.isDirty) {
                        const renderIds = this.framework.service.frame.getLayerIds(originIndex);
                        for (let i = 0; i < oldSortList.length; i++) {
                            const item = oldSortList[i];
                            if (item.id !== renderIds[item.index]) {
                                historyItem.isDirty = true;
                                break;
                            }
                        }
                    }
                }
            },
            onAfterWrite: getSendMessage,
            onAfterRedoUndo: getSendMessage,
            onMessage: (message: [number, string[]]) => {
                const [originIndex, renderIds] = message;
                const currentOriginIds = this.framework.service.frame.getLayerIds(originIndex);
                const extraGroupIds = this.framework.service.frame.getRenderIdsOnlyExtraGroup(originIndex);
                currentOriginIds.length = 0;
                currentOriginIds.push(...renderIds);
                currentOriginIds.push(...extraGroupIds);
                for (const id of currentOriginIds) {
                    const layer = this.framework.service.flatLayer.getLayer(id, true);
                    layer.parentId = undefined;
                    this.framework.service.frame.addOrUpdateLayer(layer, originIndex);
                }
                this.framework.service.frame.emitSortLayer(originIndex);
                const activeInfo = this.framework.service.frame.getActiveInfo();
                if (activeInfo?.info.id && currentOriginIds.indexOf(activeInfo.info.id) === -1) {
                    this.framework.service.frame.delActiveLayer();
                }
            },
        });
    }

    /** 锁定临时组图层，正式组通过默认记录控制 */
    private registerLockHistory() {
        const getSendMessage = (historyItem: IHistoryItem<IHistoryLockData>) => {
            return {
                message: [historyItem.data.data.item],
            };
        };

        this.framework.service.history.register(HistoryType.LOCK, {
            owner: 'layer/base',
            collaboration: 'supported',
            onRedoUndo: (historyItem: IHistoryItem<IHistoryLockData>, isRedo: boolean, isUndo: boolean) => {
                if (historyItem.isDirty) return;
                const { item: layer } = historyItem.data.data;
                if (isRedo) {
                    this.framework.service.frame.delActiveLayer();
                    const childIds = layer.children?.map((item) => item.id);
                    for (let i = 0; i < childIds.length; i++) {
                        const childId = childIds[i];
                        const childLayer = this.framework.service.flatLayer.getLayer(childId, true);
                        if (!childLayer || childLayer.parentId) continue;
                        childLayer.lock = true;
                        this.framework.service.frame.addOrUpdateLayer(childLayer);
                    }
                }
                if (isUndo) {
                    this.framework.service.frame.addOrUpdateLayer(layer);
                    this.framework.service.frame.setActiveLayer(layer.id);
                }
            },
            onBeforeRedoUndo: (historyItem: IHistoryItem<IHistoryLockData>, isRedo, isUndo, store) => {
                if (isUndo) {
                    historyItem.isDirty = this.framework.service.flatLayer.checkLayerIsDirty({
                        originLayer: historyItem.data.data.item,
                        latestIsExtraGroup: true,
                        latestIsFormalGroup: false,
                    });
                    store.set(historyItem.cursor, {
                        isDirty: historyItem.isDirty,
                    });
                }
                if (isRedo) {
                    const beforeData = store.get(historyItem.cursor);
                    if (!beforeData) {
                        historyItem.isDirty = true;
                        return;
                    }
                    historyItem.isDirty = beforeData.isDirty;
                    if (!historyItem.isDirty) {
                        historyItem.isDirty = this.framework.service.flatLayer.checkLayerIsDirty({
                            originLayer: historyItem.data.data.item,
                            latestIsExtraGroup: true,
                            latestIsFormalGroup: false,
                        });
                    }
                }
            },
            onAfterWrite: getSendMessage,
            onAfterRedoUndo: getSendMessage,
            onMessage: (message: [ILayerProps], isRedo, isUndo, isWrite) => {
                const [layer] = message;
                const activeInfo = this.framework.service.frame.getActiveInfo();
                const lockChildIds: Set<string> = new Set();
                traverseTree(layer, (item) => {
                    lockChildIds.add(item.id);
                });
                if (activeInfo) {
                    if (lockChildIds.has(activeInfo.info.id) || lockChildIds.has(activeInfo.info.pickId)) {
                        this.framework.service.frame.delActiveLayer();
                    }
                }
                if (isWrite || isRedo) {
                    const childIds = layer.children?.map((item) => item.id);
                    for (let i = 0; i < childIds.length; i++) {
                        const childId = childIds[i];
                        const childLayer = this.framework.service.flatLayer.getLayer(childId, true);
                        if (!childLayer || childLayer.parentId) continue;
                        childLayer.lock = true;
                        this.framework.service.frame.addOrUpdateLayer(childLayer);
                    }
                }

                if (isUndo) {
                    const childIds = layer.children?.map((item) => item.id);
                    for (let i = 0; i < childIds.length; i++) {
                        const childId = childIds[i];
                        const childLayer = this.framework.service.flatLayer.getLayer(childId, true);
                        if (!childLayer) continue;
                        childLayer.lock = false;
                        this.framework.service.frame.addOrUpdateLayer(childLayer);
                    }
                }
            },
        });
    }
}
